import { formatDate } from '@angular/common';		
import { Component, Inject, LOCALE_ID, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { isBuffer } from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'src/environments/environment';
import { AnalyticsService } from '../services/analytics.service';
import { GeneralService } from '../services/general.service';
import { ListItem, ReferenceData, Channel, IndicationNormalized, AssetNormalized, TA } from '../services/models';
import { ReferenceDataService } from '../services/reference-data.service';
import { execute } from '../services/utils';

const devUrlMappings = {
  '/SitePages/AEMGuidance.aspx': {
    url: '/aems'
  },
  '/SitePages/AEMGuidance.aspx?t=aemguidance': {
    url: '/aems'
  },
  '/SitePages/Access-Hub.aspx': {
    url: '/aems'
  },
  '/SitePages/AccessCommunities.aspx': {
    url: '/communities'
  },
  '/SitePages/Access-Community.aspx': {
    url: '/communities'
  },
  '/SitePages/AccessCommunities.aspx%23/?tab=guidance': {
    url: '/communities'
  },
  '/SitePages/AccessCommunities.aspx?tab=guidance': {
    url: '/communities'
  },
  '/SitePages/AccessCatalog.aspx': {
    url: '/access-catalog'
  },
  '/SitePages/PastHTADecisions.aspx': {
    url: '/past-hta-decisions'
  },
  '/SitePages/HTAEarlySAGuidance.aspx': {
    url: '/hta-es-advice'
  },
  '/SitePages/EEPGuidance.aspx': {
    url: '/ext-expert-panel'
  },
  '/SitePages/TP1-TP2Guidance.aspx': {
    url: '/tp-insights'
  },
  '/SitePages/MAAP%20Guidance.aspx': {
    url: '/market-access-trial-analysis'
  },
  '/SitePages/HTAEMCC.aspx?t=htaemcc': {
    url: '/itc-he-deliverables'
  },
  '/SitePages/HTAEducation.aspx': {
    url: '/hta-education'
  },
  '/SitePages/HTA-Trends.aspx': {
    url: '/hta-trends'
  },
  '/Lists/SubmissionTimeline/AllItems.aspx': {
    url: '/pr-timeline'
  },
  '/SitePages/HTASubmissionResourceGuidance.aspx': {
    url: '/pr-submission-resource'
  },
  '/SitePages/faqs.aspx': {
    url: '/faqs'
  },
  '/SitePages/ContractRights.aspx': {
    url: '/contract-rights'
  },
  '/SitePages/AccessAtlasCatalog.aspx': {
    url: '/access-atlas'
  }
}

const noDevUrlMappings = {
  '/SitePages/AEMGuidance.aspx': {
    url: '/aems'
  },
  '/SitePages/AEMGuidance.aspx?t=aemguidance': {
    url: '/aems'
  },
  // '/SitePages/Access-Hub.aspx': {
  //   url: '/aems'
  // },
  '/SitePages/AccessCommunities.aspx': {
    url: '/communities'
  },
  '/SitePages/Access-Community.aspx': {
    url: '/communities'
  },
  '/SitePages/AccessCommunities.aspx%23/?tab=guidance': {
    url: '/communities'
  },
  '/SitePages/AccessCommunities.aspx#/?tab=guidance': {
    url: '/communities'
  },
  '/SitePages/AccessCatalog.aspx': {
    url: '/access-catalog'
  },
  '/SitePages/PastHTADecisions.aspx': {
    url: '/past-hta-decisions'
  },
  '/SitePages/HTAEarlySAGuidance.aspx': {
    url: '/hta-es-advice'
  },
  '/SitePages/EEPGuidance.aspx': {
    url: '/ext-expert-panel'
  },
  '/SitePages/TP1-TP2Guidance.aspx': {
    url: '/tp-insights'
  },
  '/SitePages/MAAP%20Guidance.aspx': {
    url: '/market-access-trial-analysis'
  },
  '/SitePages/HTASubmissionResourceGuidance.aspx': {
    url: '/pr-submission-resource'
  },
  '/SitePages/HTAEMCC.aspx?t=htaemcc': {
    url: '/itc-he-deliverables'
  },
  '/SitePages/HTAEducation.aspx': {
    url: '/hta-education'
  },
  '/SitePages/HTA-Trends.aspx': {
    url: '/hta-trends'
  },
  '/Lists/SubmissionTimeline/AllItems.aspx': {
    url: '/pr-timeline'
  },
  '/SitePages/faqs.aspx': {
    url: '/faqs'
  },
  '/SitePages/ContractRights.aspx': {
    url: '/contract-rights'
  },
  '/SitePages/AccessAtlasCatalog.aspx': {
    url: '/access-atlas'
  }
}

const urlMappings = environment.environment === 'Development' ? devUrlMappings : noDevUrlMappings;

@Component({
  selector: 'app-channel-navigator',
  templateUrl: './channel-navigator.component.html',
  styleUrls: ['./channel-navigator.component.css']
})
export class ChannelNavigatorComponent implements OnInit {

  visibleSection = 'TaSection';
  visibleChannel = 'All';
  referenceData: ReferenceData;

  loadingChannels = false;
  viewInfo = false;

  channelDescription: SafeHtml = '';

  taTxtFilter = '';
  assetTxtFilter = '';
  indicationTxtFilter = '';

  channelType = 0;

  insightChannel: string[] = [];
  submissionChannel: string[] = [];

  srcTAs: ListItem[] = [];
  srcAssets: AssetNormalized[] = [];
  srcIndications: IndicationNormalized[] = [];

  constructor(private router: Router,
    private referenceDataService: ReferenceDataService,
    public generalService: GeneralService,
    public analyticsService: AnalyticsService,
    private toastrService: ToastrService,
    private sanitizer: DomSanitizer,
    @Inject(LOCALE_ID) protected locale: string) { }

  async ngOnInit(): Promise<void> {
    const referenceDataPromise = this.referenceDataService.get();
    try {
      this.referenceData = await referenceDataPromise;

      this.buildDataFilters();

      if (!this.generalService.filteredChannels || this.generalService.filteredChannels.length === 0)
        this.generalService.filteredChannels = this.referenceData.Channels.sort((x, y) => x.sortOrder !== y.sortOrder ? x.sortOrder - y.sortOrder : (x.Title > y.Title ? 1 : -1));
    }
    catch (error) {
      this.toastrService.show(error.message);
    }
  }

  buildDataFilters(): void {
    this.srcTAs = this.referenceData.TAs;

    this.srcAssets = [];
    this.referenceData.Assets.forEach(asset => {
      const normIdx = this.srcAssets.findIndex(x => x.Title === asset.Title);

      if (normIdx < 0) {
        this.srcAssets.push({ Title: asset.Title, Items: [asset] });
      } else {
        const normAsset = this.srcAssets[normIdx];

        if (normAsset.Items.findIndex(y => y.Id == asset.Id) === -1) {
          normAsset.Items.push(asset);
        }
      }
    });

    this.srcIndications = [];
    this.referenceData.Indications.forEach(indication => {
      const normIdx = this.srcIndications.findIndex(x => x.Title === indication.Title);

      if (normIdx < 0) {
        this.srcIndications.push({ Title: indication.Title, Items: [indication], ShortName: indication.ShortName ? indication.ShortName : '' });
      } else {
        const normIndication = this.srcIndications[normIdx];

        if (normIndication.Items.findIndex(y => y.Id == indication.Id) === -1) {
          normIndication.Items.push(indication);
        }
      }
    });
  }

  showDescription(channel: Channel): void {
    this.channelDescription = this.sanitizer.bypassSecurityTrustHtml(channel.description);
  }

  hideDescription(): void {
    this.channelDescription = '';
  }

  get channels(): Channel[] {
    if (this.channelType == 0) {
      return this.generalService.filteredChannels;
    }
    else if (this.channelType == 1) {
      return this.generalService.filteredChannels.filter(c => c.channelType == "Insight");
    }
    else {
      return this.generalService.filteredChannels.filter(c => c.channelType == "Submission");
    }
  }

  async goToChannelComponent(channel: Channel): Promise<void> {
    const listfilterSeesionObj = JSON.parse(sessionStorage.getItem('ListFilterSessionObj'));
    if(listfilterSeesionObj){
      sessionStorage.removeItem("ListFilterSessionObj");
    }

    this.generalService.buildAppliedFiltersSummary(this.srcTAs, this.srcAssets, this.srcIndications);
    const mappedUrl = urlMappings[channel.url];
    if (mappedUrl) {
      this.channelType = 0;
      if (location.pathname === mappedUrl.url) {
        await this.router.navigateByUrl('/reload', { skipLocationChange: true });
        await this.router.navigateByUrl(mappedUrl.url);
      }
      else {
        await this.router.navigateByUrl(mappedUrl.url);
        console.log(`Navigated to '${mappedUrl.url}'`);
      }
    }
    else {
      let url = environment.siteUrl;
      const urlQuery = this.buildNavigationURLFilters(channel.NavigationURL);
      if (urlQuery.length > 0)
        url += `${channel.NavigationURL}${urlQuery}`;
      else
        url += channel.url;
      // window.open(url, '_blank');
      location.href = url;
    }
    //this.analyticsService.trackChannelsAnalyticsInfo(channel.Title); 
  }
  getChannelURL(channel: Channel) {
    this.generalService.buildAppliedFiltersSummary(this.srcTAs, this.srcAssets, this.srcIndications);
   if(channel  && channel.url){
    const mappedUrl = urlMappings[channel.url];
    if(mappedUrl && mappedUrl.url){
    const channelUrl = mappedUrl.url;
    return channelUrl;
    }
   } 
   return '';
  }
  reloadSelectedFilters(): void {
    const selectedTAs = this.generalService.selectedTAIds;
    const selectedAssets = this.srcAssets.filter(x => this.generalService.selectedAssetIds.length > 0 && this.generalService.selectedAssetIds.findIndex(y => y.Title === x.Title) > -1);
    this.generalService.selectedAssetIds = selectedAssets.filter(normAsset => {
      return selectedTAs.findIndex(ta => normAsset.Items.some(item => item.TAId == ta)) != -1;
    }).map(x => {
      return { Title: x.Title, Ids: x.Items.map(y => y.Id) };
    });

    const selectedIndications = this.srcIndications.filter(x => this.generalService.selectedIndicationIds.length > 0 && this.generalService.selectedIndicationIds.findIndex(y => y.Title === x.Title) > -1);
    this.generalService.selectedIndicationIds = selectedIndications.filter(normIndication => {
      return selectedTAs.findIndex(ta => normIndication.Items.some(item => item.TAId == ta)) != -1;
    }).map(x => {
      return { Title: x.Title, Ids: x.Items.map(y => y.Id) };
    });
  }

  get filteredTAs(): TA[] {
    if (!this.referenceData || !this.referenceData.TAs) return [];
    if (!this.taTxtFilter || this.taTxtFilter.length === 0) return this.referenceData.TAs.sort((x, y) => x.Title > y.Title ? 1 : -1);
    return this.referenceData.TAs.filter(x => x.Title.toLocaleLowerCase().search(this.taTxtFilter.toLocaleLowerCase()) >= 0).sort((x, y) => x.SortOrder !== y.SortOrder ? x.SortOrder - y.SortOrder : (x.Title > y.Title ? 1 : -1));
  }

  get filteredAssets(): AssetNormalized[] {
    if (!this.referenceData || this.srcTAs.length === 0 || this.srcAssets.length === 0)
      return [];

    let result = this.srcAssets;

    if (this.generalService.selectedTAIds.length > 0)
      result = result.filter(x => x.Items.some(y => this.generalService.selectedTAIds.findIndex(z => z == y.TAId) != -1));

    if (this.assetTxtFilter && this.assetTxtFilter.length > 0)
      result = result.filter(x => x.Title.toLocaleLowerCase().search(this.assetTxtFilter.toLocaleLowerCase()) >= 0);

    return result.sort((x, y) => x.Title > y.Title ? 1 : -1);
  }

  get filteredIndications(): IndicationNormalized[] {
    if (!this.referenceData
      || !this.referenceData.TAs || this.referenceData.TAs.length === 0
      || !this.referenceData.Indications || this.referenceData.Indications.length === 0)
      return [];

    let result = this.srcIndications;

    if (this.generalService.selectedTAIds.length > 0)
      result = result.filter(x => x.Items.some(y => this.generalService.selectedTAIds.findIndex(z => z == y.TAId) != -1));

    if (this.indicationTxtFilter && this.indicationTxtFilter.length > 0)
      result = result.filter(x => x.Title.toLocaleLowerCase().search(this.indicationTxtFilter.toLocaleLowerCase()) >= 0 || x.ShortName.toLocaleLowerCase().search(this.indicationTxtFilter.toLocaleLowerCase()) >= 0);

    return result.sort((x, y) => x.Title > y.Title ? 1 : -1);
  }

  onSectionClick(section: string): void {
    if (this.visibleSection === section) return;

    this.visibleSection = section;
    this.clearTextInputs();
  }

  clearTextInputs(): void {
    this.taTxtFilter = '';
    this.assetTxtFilter = '';
    this.indicationTxtFilter = '';
  }

  clearAllFilters(): void {
    this.generalService.filteredChannels = this.referenceData.Channels.sort((x, y) => x.sortOrder !== y.sortOrder ? x.sortOrder - y.sortOrder : (x.Title > y.Title ? 1 : -1));
    this.visibleSection = 'TaSection';
    this.clearTextInputs();
    this.generalService.clearAll();
  }

  async applyChannelFilters(): Promise<void> {
    try {
      this.loadingChannels = true;
      const channels = await execute(() => { return this.generalService.applyChannelFilters(this.referenceData.Channels) });
      this.generalService.filteredChannels = channels.sort((x, y) => x.sortOrder !== y.sortOrder ? x.sortOrder - y.sortOrder : (x.Title > y.Title ? 1 : -1));
    }
    catch (error) {
      this.loadingChannels = false;
      this.toastrService.show(error.message)
    }
    finally {
      this.loadingChannels = false;
    }
  }

  buildNavigationURLFilters(navigationUrl: string): string {
    let fieldIdx = 1;
    let query = navigationUrl.search(/\?t=/gm) !== -1 ? '&' : '?';

    if (this.generalService.selectedTAIds.length > 0) {
      query += `FilterField${fieldIdx}=TA&FilterValue${fieldIdx}=`;
      const taValues = this.generalService.selectedTAIds.map(x => this.referenceData.TAsById[x].Title).join(';#');
      query += taValues;
      fieldIdx++;
    }

    if (this.generalService.selectedAssetIds.length > 0) {
      if (fieldIdx > 1) query += "&";
      query += `FilterField${fieldIdx}=Asset&FilterValue${fieldIdx}=`;
      const taValues = this.generalService.selectedAssetIds.map(x => x.Title).join(';#');
      query += taValues;
      fieldIdx++;
    }

    if (this.generalService.selectedIndicationIds.length > 0) {
      if (fieldIdx > 1) query += "&";
      query += `FilterField${fieldIdx}=Indication&FilterValue${fieldIdx}=`;
      const taValues = this.generalService.selectedIndicationIds.map(x => x.Title).join(';#');
      query += taValues;
      fieldIdx++;
    }
    return fieldIdx === 1 ? '' : query;
  }
  get tasFilterCountResume(): number { return this.generalService.selectedTAIds.length - 1; }

  get tasFilterText(): string {
    if (!this.referenceData) return '';

    const filter = this.referenceData.TAs.filter(x => this.generalService.selectedTAIds.includes(x.Id));
    return filter.length > 0 ? filter[0].Title : '';
  }

  get assetsFilterCountResume(): number { return this.generalService.selectedAssetIds.length - 1; }

  get assetsFilterText(): string {
    const filter = this.srcAssets.filter(x => this.generalService.selectedAssetIds.some(y => y.Title === x.Title))
    return filter.length > 0 ? filter[0].Title : '';
  }

  get indicationsFilterCountResume(): number { return this.generalService.selectedIndicationIds.length - 1; }

  get indicationsFilterText(): string {
    const filter = this.srcIndications.filter(x => this.generalService.selectedIndicationIds.some(y => y.Title === x.Title));
    return filter.length > 0 ? filter[0].Title : '';
  }

  getItemLabel(id: string, checkbox: string): string {
    return `${checkbox}${id.replace(/ */g, "",)}`
  }

  getNewLableVisible(item){    		
    if(item.NewTextEnabled == true && (formatDate(new Date(),'yyyy-MM-ddThh:mm:ss', this.locale) < formatDate(item.NewLabelExpiryDate,'yyyy-MM-ddThh:mm:ss', this.locale))){		
      return true;		
    }		
    else{		
      return false;		
    }		
  }
}
